<template>
  <ol class="flex w-full" aria-label="Import steps" data-cy="import-steps">
    <li
      v-for="(label, index) in STEP_LABELS"
      :key="label"
      class="min-w-0 flex-1"
      :data-cy="`import-step-${index}`"
      :data-state="stateOf(index)"
      :aria-current="index === current ? 'step' : undefined"
    >
      <div class="flex items-center">
        <span
          class="h-4 w-4 shrink-0 rounded-full"
          :class="DOT_CLASSES[stateOf(index)]"
        />
        <span
          v-if="index < STEP_LABELS.length - 1"
          class="mx-1 h-0.5 flex-1"
          :class="index < current ? 'bg-blue-600' : 'bg-gray-200'"
          data-cy="import-step-line"
        />
      </div>
      <span
        v-if="!isMobile || index === current"
        class="mt-2 block whitespace-nowrap text-sm md:text-base"
        :class="LABEL_CLASSES[stateOf(index)]"
      >
        {{ label }}
      </span>
    </li>
  </ol>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { useBreakpoints } from "@/composables/useBreakpoints";

type StepState = "done" | "current" | "upcoming" | "skipped";

const STEP_LABELS = ["Upload", "Map columns", "Review", "Import"];

const DOT_CLASSES: Record<StepState, string> = {
  done: "bg-blue-600 ring-4 ring-blue-100",
  current: "bg-blue-600 ring-4 ring-blue-100",
  upcoming: "bg-gray-900 ring-4 ring-gray-100",
  skipped: "border-2 border-gray-300 bg-white"
};

const LABEL_CLASSES: Record<StepState, string> = {
  done: "text-gray-900",
  current: "font-semibold text-gray-900",
  upcoming: "text-gray-900",
  skipped: "text-gray-400"
};

const props = withDefaults(
  defineProps<{
    /** Zero-based index of the step the admin is on; one past the last marks every step done. */
    current: number;
    /** Steps this import never visits (a vendor upload skips "Map columns"). */
    skipped?: number[];
  }>(),
  { skipped: () => [] }
);

const { type } = useBreakpoints();
const isMobile = computed(() => type.value === "mobile");

function stateOf(index: number): StepState {
  if (index === props.current) return "current";
  if (props.skipped.includes(index)) return "skipped";
  return index < props.current ? "done" : "upcoming";
}
</script>
